import { Feather } from "@expo/vector-icons";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Image,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import type { ShopOrder, ShopProduct } from "../../types";
import {
  checkoutShop,
  fetchPaidAccountSession,
  fetchShopProduct,
  PaidVideoApiError,
  type PaidAccount,
} from "../services/videoServer";

type CartLine = { product: ShopProduct; quantity: number };
type ShopView = "product" | "cart" | "checkout" | "success";

type ShopContextValue = {
  cartCount: number;
  addToCart: (product: ShopProduct) => void;
  openCart: () => void;
  openProduct: (product: ShopProduct) => void;
  buyNow: (product: ShopProduct) => void;
};

const CART_STORAGE_KEY = "neptune-shop-cart-v1";
const ShopContext = createContext<ShopContextValue | null>(null);

function loadCart(): CartLine[] {
  if (Platform.OS !== "web" || typeof window === "undefined") return [];
  try {
    const value = JSON.parse(
      window.localStorage.getItem(CART_STORAGE_KEY) || "[]",
    );
    return Array.isArray(value)
      ? value.filter(
          (line) =>
            line?.product?.productId &&
            Number.isFinite(Number(line?.quantity)) &&
            Number(line.quantity) > 0,
        )
      : [];
  } catch {
    return [];
  }
}

function requestId() {
  return (
    globalThis.crypto?.randomUUID?.() ||
    `shop-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
}

function ProductImage({
  product,
  style,
}: {
  product: ShopProduct;
  style: any;
}) {
  if (product.imageUrl) {
    return (
      <Image
        source={{ uri: product.imageUrl }}
        style={style}
        resizeMode="cover"
      />
    );
  }
  return (
    <View style={[style, styles.imageFallback]}>
      <Feather name="shopping-bag" size={34} color="#d7b65d" />
    </View>
  );
}

export function ShopProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<CartLine[]>(loadCart);
  const [visible, setVisible] = useState(false);
  const [view, setView] = useState<ShopView>("product");
  const [selectedProduct, setSelectedProduct] = useState<ShopProduct | null>(
    null,
  );
  const [checkoutLines, setCheckoutLines] = useState<CartLine[]>([]);
  const [checkoutId, setCheckoutId] = useState("");
  const [account, setAccount] = useState<PaidAccount | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [lastOrder, setLastOrder] = useState<ShopOrder | null>(null);

  useEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") return;
    try {
      window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
    } catch {}
  }, [cart]);

  const refreshAccount = () =>
    fetchPaidAccountSession()
      .then(setAccount)
      .catch(() => setAccount(null));

  const addToCart = (product: ShopProduct) => {
    if (product.stock < 1) return;
    setCart((current) => {
      const existing = current.find(
        (line) => line.product.productId === product.productId,
      );
      if (!existing) return [...current, { product, quantity: 1 }];
      return current.map((line) =>
        line.product.productId === product.productId
          ? { ...line, quantity: Math.min(product.stock, line.quantity + 1) }
          : line,
      );
    });
    setMessage("已加入购物车");
  };

  const openProduct = (product: ShopProduct) => {
    setSelectedProduct(product);
    setView("product");
    setVisible(true);
    setMessage("");
    void refreshAccount();
    void fetchShopProduct(product.productId)
      .then((fresh) => setSelectedProduct(fresh))
      .catch(() => {});
  };

  const startCheckout = (lines: CartLine[]) => {
    setCheckoutLines(lines);
    setCheckoutId(requestId());
    setMessage("");
    setView("checkout");
    setVisible(true);
    void refreshAccount();
  };

  const buyNow = (product: ShopProduct) => {
    if (product.stock < 1) {
      openProduct(product);
      setMessage("商品暂时缺货");
      return;
    }
    setSelectedProduct(product);
    startCheckout([{ product, quantity: 1 }]);
  };

  const openCart = () => {
    setView("cart");
    setVisible(true);
    setMessage("");
    void refreshAccount();
  };

  const cartCount = cart.reduce((sum, line) => sum + line.quantity, 0);
  const checkoutTotal = checkoutLines.reduce(
    (sum, line) => sum + line.product.pointPrice * line.quantity,
    0,
  );

  const updateQuantity = (productId: string, delta: number) => {
    setCart((current) =>
      current.flatMap((line) => {
        if (line.product.productId !== productId) return [line];
        const quantity = Math.max(
          0,
          Math.min(line.product.stock, line.quantity + delta),
        );
        return quantity ? [{ ...line, quantity }] : [];
      }),
    );
  };

  const completeCheckout = async () => {
    if (!checkoutLines.length || saving) return;
    setSaving(true);
    setMessage(`正在支付 ${checkoutTotal} 点…`);
    try {
      const result = await checkoutShop(
        checkoutLines.map((line) => ({
          productId: line.product.productId,
          quantity: line.quantity,
        })),
        checkoutId,
      );
      setAccount(result.account);
      setLastOrder(result.order);
      const purchasedIds = new Set(
        checkoutLines.map((line) => line.product.productId),
      );
      setCart((current) =>
        current.filter((line) => !purchasedIds.has(line.product.productId)),
      );
      setMessage(result.duplicate ? "订单已支付，请勿重复提交" : "支付成功");
      setView("success");
    } catch (error) {
      if (error instanceof PaidVideoApiError && error.account) {
        setAccount(error.account);
      }
      setMessage(error instanceof Error ? error.message : "结算失败，请重试");
    } finally {
      setSaving(false);
    }
  };

  const contextValue = useMemo<ShopContextValue>(
    () => ({ cartCount, addToCart, openCart, openProduct, buyNow }),
    [cartCount],
  );

  return (
    <ShopContext.Provider value={contextValue}>
      {children}
      <Modal
        transparent
        visible={visible}
        animationType="fade"
        onRequestClose={() => setVisible(false)}
      >
        <View style={styles.backdrop}>
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={() => setVisible(false)}
          />
          <View style={styles.sheet}>
            <View style={styles.header}>
              <View style={styles.headerCopy}>
                <Text style={styles.eyebrow}>NEPTUNE SELECT</Text>
                <Text style={styles.heading}>
                  {view === "product"
                    ? "商品详情"
                    : view === "cart"
                    ? "购物车"
                    : view === "checkout"
                    ? "确认点数购买"
                    : "购买完成"}
                </Text>
              </View>
              <View style={styles.headerActions}>
                <Pressable
                  style={styles.balancePill}
                  onPress={() => void refreshAccount()}
                >
                  <Feather name="hexagon" size={14} color="#d7b65d" />
                  <Text style={styles.balanceText}>
                    {account ? `${account.balance} 点` : "未验证"}
                  </Text>
                </Pressable>
                <Pressable style={styles.cartButton} onPress={openCart}>
                  <Feather name="shopping-cart" size={18} color="#eef5f2" />
                  {cartCount > 0 ? (
                    <Text style={styles.cartBadge}>{cartCount}</Text>
                  ) : null}
                </Pressable>
                <Pressable
                  style={styles.closeButton}
                  onPress={() => setVisible(false)}
                >
                  <Feather name="x" size={21} color="#aab5b1" />
                </Pressable>
              </View>
            </View>

            <ScrollView contentContainerStyle={styles.content}>
              {view === "product" && selectedProduct ? (
                <View style={styles.productPage}>
                  <ProductImage
                    product={selectedProduct}
                    style={styles.heroImage}
                  />
                  <View style={styles.productCopy}>
                    <View style={styles.badgeRow}>
                      {selectedProduct.badge ? (
                        <Text style={styles.productBadge}>
                          {selectedProduct.badge}
                        </Text>
                      ) : null}
                      <Text style={styles.stockText}>
                        {selectedProduct.stock > 0
                          ? `库存 ${selectedProduct.stock}`
                          : "暂时缺货"}
                      </Text>
                    </View>
                    <Text style={styles.productName}>
                      {selectedProduct.name}
                    </Text>
                    {selectedProduct.subtitle ? (
                      <Text style={styles.productSubtitle}>
                        {selectedProduct.subtitle}
                      </Text>
                    ) : null}
                    <View style={styles.priceRow}>
                      <Text style={styles.price}>
                        {selectedProduct.pointPrice}
                      </Text>
                      <Text style={styles.priceUnit}>点</Text>
                      {selectedProduct.originalPointPrice >
                      selectedProduct.pointPrice ? (
                        <Text style={styles.originalPrice}>
                          {selectedProduct.originalPointPrice} 点
                        </Text>
                      ) : null}
                    </View>
                    <Text style={styles.description}>
                      {selectedProduct.description ||
                        "视频关联精选商品，使用账户点数即可完成购买。"}
                    </Text>
                    <View style={styles.primaryActions}>
                      <Pressable
                        disabled={selectedProduct.stock < 1}
                        style={[
                          styles.secondaryAction,
                          selectedProduct.stock < 1 && styles.disabled,
                        ]}
                        onPress={() => addToCart(selectedProduct)}
                      >
                        <Feather
                          name="shopping-cart"
                          size={17}
                          color="#d7b65d"
                        />
                        <Text style={styles.secondaryActionText}>
                          加入购物车
                        </Text>
                      </Pressable>
                      <Pressable
                        disabled={selectedProduct.stock < 1}
                        style={[
                          styles.primaryAction,
                          selectedProduct.stock < 1 && styles.disabled,
                        ]}
                        onPress={() => buyNow(selectedProduct)}
                      >
                        <Text style={styles.primaryActionText}>点数购买</Text>
                      </Pressable>
                    </View>
                  </View>
                  {selectedProduct.detailImages.map((url) => (
                    <Image
                      key={url}
                      source={{ uri: url }}
                      style={styles.detailImage}
                      resizeMode="cover"
                    />
                  ))}
                </View>
              ) : null}

              {view === "cart" ? (
                cart.length ? (
                  <View style={styles.cartList}>
                    {cart.map((line) => (
                      <View
                        key={line.product.productId}
                        style={styles.cartLine}
                      >
                        <ProductImage
                          product={line.product}
                          style={styles.cartImage}
                        />
                        <View style={styles.cartCopy}>
                          <Text style={styles.cartName} numberOfLines={2}>
                            {line.product.name}
                          </Text>
                          <Text style={styles.cartPrice}>
                            {line.product.pointPrice} 点
                          </Text>
                        </View>
                        <View style={styles.quantityControl}>
                          <Pressable
                            style={styles.quantityButton}
                            onPress={() =>
                              updateQuantity(line.product.productId, -1)
                            }
                          >
                            <Feather name="minus" size={14} color="#eaf2ef" />
                          </Pressable>
                          <Text style={styles.quantity}>{line.quantity}</Text>
                          <Pressable
                            style={styles.quantityButton}
                            onPress={() =>
                              updateQuantity(line.product.productId, 1)
                            }
                          >
                            <Feather name="plus" size={14} color="#eaf2ef" />
                          </Pressable>
                        </View>
                      </View>
                    ))}
                    <View style={styles.cartFooter}>
                      <View>
                        <Text style={styles.totalLabel}>合计</Text>
                        <Text style={styles.totalValue}>
                          {cart.reduce(
                            (sum, line) =>
                              sum + line.product.pointPrice * line.quantity,
                            0,
                          )}{" "}
                          点
                        </Text>
                      </View>
                      <Pressable
                        style={styles.primaryAction}
                        onPress={() => startCheckout(cart)}
                      >
                        <Text style={styles.primaryActionText}>去结算</Text>
                      </Pressable>
                    </View>
                  </View>
                ) : (
                  <View style={styles.emptyState}>
                    <Feather name="shopping-cart" size={40} color="#5f6c67" />
                    <Text style={styles.emptyTitle}>购物车还是空的</Text>
                    <Text style={styles.emptyText}>
                      视频播放到商品时间点时，可以直接加入。
                    </Text>
                  </View>
                )
              ) : null}

              {view === "checkout" ? (
                <View style={styles.checkoutCard}>
                  {checkoutLines.map((line) => (
                    <View
                      key={line.product.productId}
                      style={styles.checkoutLine}
                    >
                      <Text style={styles.checkoutName} numberOfLines={1}>
                        {line.product.name} × {line.quantity}
                      </Text>
                      <Text style={styles.checkoutPrice}>
                        {line.product.pointPrice * line.quantity} 点
                      </Text>
                    </View>
                  ))}
                  <View style={styles.checkoutTotal}>
                    <Text style={styles.totalLabel}>应付点数</Text>
                    <Text style={styles.checkoutTotalValue}>
                      {checkoutTotal} 点
                    </Text>
                  </View>
                  <Text style={styles.accountHint}>
                    {account
                      ? `账户 ${account.label || account.email} · 可用 ${
                          account.balance
                        } 点`
                      : "尚未验证购买账号，请先在账号中心完成验证登录。"}
                  </Text>
                  {message ? (
                    <Text style={styles.message}>{message}</Text>
                  ) : null}
                  <Pressable
                    disabled={saving}
                    style={[styles.confirmButton, saving && styles.disabled]}
                    onPress={() => void completeCheckout()}
                  >
                    <Feather name="shield" size={18} color="#17120b" />
                    <Text style={styles.confirmText}>
                      {saving ? "正在结算…" : `确认支付 ${checkoutTotal} 点`}
                    </Text>
                  </Pressable>
                </View>
              ) : null}

              {view === "success" ? (
                <View style={styles.successState}>
                  <View style={styles.successIcon}>
                    <Feather name="check" size={32} color="#06130f" />
                  </View>
                  <Text style={styles.successTitle}>购买成功</Text>
                  <Text style={styles.successText}>
                    订单 {lastOrder?.id || "已创建"} · 已支付{" "}
                    {lastOrder?.totalPoints || checkoutTotal} 点
                  </Text>
                  <Text style={styles.successBalance}>
                    账户剩余 {account?.balance ?? "—"} 点
                  </Text>
                  <Pressable
                    style={styles.primaryAction}
                    onPress={() => setVisible(false)}
                  >
                    <Text style={styles.primaryActionText}>完成</Text>
                  </Pressable>
                </View>
              ) : null}

              {view !== "checkout" && message ? (
                <Text style={styles.message}>{message}</Text>
              ) : null}
            </ScrollView>
          </View>
        </View>
      </Modal>
    </ShopContext.Provider>
  );
}

export function useShop() {
  const value = useContext(ShopContext);
  if (!value) throw new Error("useShop must be used inside ShopProvider");
  return value;
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 18,
    backgroundColor: "rgba(0,0,0,.78)",
  },
  sheet: {
    width: "100%",
    maxWidth: 760,
    maxHeight: "90%",
    overflow: "hidden",
    borderRadius: 22,
    backgroundColor: "#07110e",
    borderWidth: 1,
    borderColor: "#3b6e5c",
  },
  header: {
    minHeight: 72,
    paddingHorizontal: 20,
    paddingVertical: 12,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottomWidth: 1,
    borderBottomColor: "#1c4035",
    backgroundColor: "#091712",
  },
  headerCopy: { flex: 1, minWidth: 0 },
  eyebrow: {
    color: "#28c99a",
    fontSize: 9,
    fontWeight: "900",
    letterSpacing: 1.8,
  },
  heading: { marginTop: 3, color: "#f2f5f4", fontSize: 20, fontWeight: "900" },
  headerActions: { flexDirection: "row", alignItems: "center", gap: 7 },
  balancePill: {
    height: 34,
    paddingHorizontal: 10,
    borderRadius: 17,
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    backgroundColor: "#201b12",
    borderWidth: 1,
    borderColor: "#65502e",
  },
  balanceText: { color: "#e8cc92", fontSize: 12, fontWeight: "800" },
  cartButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#10231d",
  },
  cartBadge: {
    position: "absolute",
    top: -3,
    right: -3,
    minWidth: 17,
    height: 17,
    paddingHorizontal: 4,
    borderRadius: 9,
    color: "#17120b",
    backgroundColor: "#d7b65d",
    fontSize: 9,
    lineHeight: 17,
    fontWeight: "900",
    textAlign: "center",
  },
  closeButton: {
    width: 34,
    height: 34,
    alignItems: "center",
    justifyContent: "center",
  },
  content: { padding: 20, paddingBottom: 28 },
  productPage: { flexDirection: "row", flexWrap: "wrap", gap: 22 },
  heroImage: {
    width: 280,
    maxWidth: "100%",
    aspectRatio: 1,
    borderRadius: 16,
    backgroundColor: "#15211d",
  },
  imageFallback: {
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#14211c",
  },
  productCopy: { flex: 1, minWidth: 250, justifyContent: "center" },
  badgeRow: { flexDirection: "row", alignItems: "center", gap: 9 },
  productBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 5,
    color: "#17120b",
    backgroundColor: "#d7b65d",
    fontSize: 10,
    fontWeight: "900",
  },
  stockText: { color: "#86928d", fontSize: 11 },
  productName: {
    marginTop: 11,
    color: "#f6f4ef",
    fontSize: 25,
    lineHeight: 33,
    fontWeight: "900",
  },
  productSubtitle: { marginTop: 5, color: "#9ba6a2", fontSize: 14 },
  priceRow: {
    marginTop: 16,
    flexDirection: "row",
    alignItems: "baseline",
    gap: 4,
  },
  price: { color: "#e4bd68", fontSize: 34, fontWeight: "900" },
  priceUnit: { color: "#e4bd68", fontSize: 15, fontWeight: "800" },
  originalPrice: {
    marginLeft: 8,
    color: "#65706c",
    fontSize: 12,
    textDecorationLine: "line-through",
  },
  description: {
    marginTop: 13,
    color: "#adb6b2",
    fontSize: 13,
    lineHeight: 21,
  },
  primaryActions: {
    marginTop: 20,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
  primaryAction: {
    minWidth: 118,
    height: 42,
    paddingHorizontal: 18,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#d7b65d",
  },
  primaryActionText: { color: "#17120b", fontSize: 14, fontWeight: "900" },
  secondaryAction: {
    minWidth: 138,
    height: 42,
    paddingHorizontal: 15,
    borderRadius: 10,
    flexDirection: "row",
    gap: 7,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "#6a5430",
    backgroundColor: "#1b1812",
  },
  secondaryActionText: { color: "#e5c888", fontSize: 13, fontWeight: "800" },
  disabled: { opacity: 0.48 },
  detailImage: {
    width: "100%",
    minHeight: 260,
    aspectRatio: 16 / 9,
    borderRadius: 14,
    backgroundColor: "#14211c",
  },
  cartList: { gap: 10 },
  cartLine: {
    minHeight: 86,
    padding: 10,
    borderRadius: 13,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    backgroundColor: "#0c1915",
    borderWidth: 1,
    borderColor: "#24463a",
  },
  cartImage: { width: 66, height: 66, borderRadius: 10 },
  cartCopy: { flex: 1, minWidth: 0 },
  cartName: { color: "#edf2f0", fontSize: 14, fontWeight: "800" },
  cartPrice: {
    marginTop: 7,
    color: "#d7b65d",
    fontSize: 13,
    fontWeight: "900",
  },
  quantityControl: { flexDirection: "row", alignItems: "center", gap: 5 },
  quantityButton: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#173026",
  },
  quantity: {
    minWidth: 24,
    color: "white",
    fontSize: 13,
    fontWeight: "800",
    textAlign: "center",
  },
  cartFooter: {
    marginTop: 8,
    paddingTop: 17,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderTopWidth: 1,
    borderTopColor: "#26443a",
  },
  totalLabel: { color: "#88948f", fontSize: 11 },
  totalValue: {
    marginTop: 3,
    color: "#f0d08d",
    fontSize: 22,
    fontWeight: "900",
  },
  emptyState: {
    minHeight: 280,
    alignItems: "center",
    justifyContent: "center",
  },
  emptyTitle: {
    marginTop: 14,
    color: "#e7ecea",
    fontSize: 18,
    fontWeight: "900",
  },
  emptyText: {
    marginTop: 7,
    color: "#7c8984",
    fontSize: 13,
    textAlign: "center",
  },
  checkoutCard: {
    padding: 18,
    borderRadius: 16,
    backgroundColor: "#0c1b16",
    borderWidth: 1,
    borderColor: "#315c4d",
  },
  checkoutLine: {
    minHeight: 42,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#1d3a30",
  },
  checkoutName: { flex: 1, color: "#dfe7e4", fontSize: 13, fontWeight: "700" },
  checkoutPrice: { color: "#d7b65d", fontSize: 13, fontWeight: "900" },
  checkoutTotal: {
    marginTop: 14,
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "space-between",
  },
  checkoutTotalValue: { color: "#f0d08d", fontSize: 27, fontWeight: "900" },
  accountHint: {
    marginTop: 16,
    padding: 12,
    borderRadius: 9,
    color: "#b6c0bc",
    fontSize: 12,
    lineHeight: 18,
    backgroundColor: "#12251e",
  },
  message: {
    marginTop: 12,
    color: "#e8c984",
    fontSize: 12,
    textAlign: "center",
  },
  confirmButton: {
    height: 48,
    marginTop: 17,
    borderRadius: 11,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    backgroundColor: "#d7b65d",
  },
  confirmText: { color: "#17120b", fontSize: 15, fontWeight: "900" },
  successState: {
    minHeight: 310,
    alignItems: "center",
    justifyContent: "center",
  },
  successIcon: {
    width: 62,
    height: 62,
    borderRadius: 31,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#28c99a",
  },
  successTitle: {
    marginTop: 17,
    color: "#f2f5f4",
    fontSize: 25,
    fontWeight: "900",
  },
  successText: {
    marginTop: 8,
    color: "#9fa9a5",
    fontSize: 13,
    textAlign: "center",
  },
  successBalance: {
    marginTop: 7,
    marginBottom: 20,
    color: "#e4bd68",
    fontSize: 14,
    fontWeight: "800",
  },
});
